/**
 * safeFetch — XHR-based wrapper to avoid Emergent platform's fetch() interception.
 * The platform monitoring script consumes fetch response bodies, breaking .json() and .blob().
 * This module provides drop-in replacements using XMLHttpRequest.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Make a JSON API request and return parsed data.
 * @param {string} url — relative (/api/...) or absolute URL
 * @param {object} options — { method, headers, body, token }
 */
export function safeJsonRequest(url, options = {}) {
  return new Promise((resolve, reject) => {
    const fullUrl = url.startsWith("http") ? url : `${API_URL}${url}`;
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "GET", fullUrl, true);
    xhr.withCredentials = true;

    // Default headers
    xhr.setRequestHeader("Content-Type", "application/json");
    if (options.token) {
      xhr.setRequestHeader("Authorization", `Bearer ${options.token}`);
    }
    // Custom headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          resolve(xhr.responseText);
        }
      } else {
        let detail = xhr.responseText;
        try {
          const parsed = JSON.parse(xhr.responseText);
          detail = parsed.detail || JSON.stringify(parsed);
        } catch { /* use raw text */ }
        reject({ status: xhr.status, detail });
      }
    };

    xhr.onerror = () => reject({ status: 0, detail: "Network error" });
    xhr.ontimeout = () => reject({ status: 0, detail: "Request timed out" });
    xhr.timeout = 30000;

    xhr.send(options.body ? (typeof options.body === "string" ? options.body : JSON.stringify(options.body)) : null);
  });
}

export default safeJsonRequest;
